import { useState, useMemo, Dispatch, useCallback } from 'react';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import { connect, useDispatch } from 'react-redux';
import { FormSpy } from 'react-final-form';

import { ErknmKnmDocumentTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/ErknmKnmDocumentTypeCodesEnum';
import { IKnmAttachment } from 'APIPackage/KnmModule/interfaces/IKnmAttachment';
import { getCatalogDecisionDocumentTypesSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IKnmDocumentTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmDocumentTypeSelectOption';
import { KNM_DOCUMENTS } from 'CorePackage/MainModule/routes/routes';
import { getDocumentBlockId } from 'KnmPackage/MainModule/constants/fieldsElementsIds';
import {
  getKnmIsErknmStatusApproving,
  getKnmIsErknmStatusCommentsExist,
  getKnmIsErknmStatusDecisionAppealed,
  getKnmIsErknmStatusFillInProcess,
  getKnmIsErknmStatusNotApprove,
  getKnmIsErknmStatusReadyForApprove,
  getKnmIsErknmStatusRejectAppealed,
  getKnmIsErknmStatusTypeCantBeCarryOut,
  getKnmIsErknmStatusTypeCarriedOut,
  getKnmIsErknmStatusTypeCompleted,
  getKnmIsErknmStatusTypeWaitingCarryOut,
  getKnmIsErpStatusDeleted,
  getKnmSystemType,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { KnmSignDocumentModal } from 'KnmPackage/KnmDocumentsModule/components/KnmSignDocumentModal';
import { KnmSystemTypesEnum } from 'KnmPackage/MainModule/enums/KnmSystemTypesEnum';
import { KnmAttachmentsList } from 'KnmPackage/MainModule/components/KnmAttachmentsList';
import { IKnmTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmTypeSelectOption';
import { IDictionaryKnmKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryKnmKindSelectOption';
import { DocumentsTypeCodesEnum } from 'KnmErknmPackage/MainModule/enums/DocumentsTypeCodesEnum';
import { Section } from 'UIPackage/FormModule/components/Section';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import {
  getUserRoleIsProsecutorOrSuperProsecutor,
  getUserRoleIsSupervisor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { IDecisionFormRenderProps } from 'DecisionPackage/MainModule/components/DecisionForm';
import styles from 'KnmPackage/KnmDocumentsModule/components/KnmDocuments.module.scss';
import { clippedKnmDocumentTypes } from 'KnmPackage/KnmDocumentsModule/common/clippedKnmDocumentTypes';
import { IKnmAttachmentDocument } from 'KnmPackage/KnmDocumentsModule/interfaces/IKnmAttachmentDocument';
import { IKnmFormDocument } from 'KnmPackage/KnmDocumentsModule/interfaces/IKnmFormDocument';
import { knmDocumentsSaveSignedDocument } from 'KnmPackage/KnmDocumentsModule/actions/knmDocumentsActions';

interface IDecisionDocumentsProps {
  watchOnly?: boolean;
  formInstance?: IDecisionFormRenderProps['form'];
  title: string;
  reasonDocument?: boolean;
  disableDeleteDocuments?: boolean;
  typeErknm?: IKnmTypeSelectOption;
  kindKnm?: IDictionaryKnmKindSelectOption;
}

interface IDecisionDocumentsStoreProps {
  knmSystemType: KnmSystemTypesEnum | undefined;
  isProsecutorOrSuperProsecutor: boolean;
  isSupervisor: boolean;
  isKnmStatusFillInProcess: boolean;
  isKnmStatusReadyForApprove: boolean;
  isKnmStatusApproving: boolean;
  isKnmStatusNotApprove: boolean;
  isKnmStatusCommentsExist: boolean;
  isKnmStatusRejectAppealed: boolean;
  isKnmStatusTypeWaitingCarryOut: boolean;
  isKnmStatusTypeCarriedOut: boolean;
  isKnmStatusTypeCompleted: boolean;
  isKnmStatusDecisionAppealed: boolean;
  isKnmStatusTypeCantBeCarryOut: boolean;
  isKnmStatusTypeDeleted: boolean;
  knmDocumentTypesSelectOptions: IKnmDocumentTypeSelectOption[];
}

function DecisionDocumentsWithoutStore({
  watchOnly,
  formInstance,
  title,
  reasonDocument,
  disableDeleteDocuments,
  typeErknm,
  kindKnm,
  knmDocumentTypesSelectOptions,
  knmSystemType,
  isProsecutorOrSuperProsecutor,
  isSupervisor,
  isKnmStatusFillInProcess,
  isKnmStatusReadyForApprove,
  isKnmStatusApproving,
  isKnmStatusNotApprove,
  isKnmStatusCommentsExist,
  isKnmStatusRejectAppealed,
  isKnmStatusTypeWaitingCarryOut,
  isKnmStatusTypeCarriedOut,
  isKnmStatusTypeCompleted,
  isKnmStatusDecisionAppealed,
  isKnmStatusTypeCantBeCarryOut,
  isKnmStatusTypeDeleted,
}: IDecisionDocumentsProps & IDecisionDocumentsStoreProps) {
  const form = formInstance as IDecisionFormRenderProps['form'] | undefined;
  const isErknmSystemType = knmSystemType === KnmSystemTypesEnum.ERKNM;
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const push = form?.mutators.push;
  const remove = form?.mutators.remove;

  const filteredKnmDocumentTypesSelectOptions: IKnmDocumentTypeSelectOption[] = useMemo(() => {
    if (reasonDocument) {
      return knmDocumentTypesSelectOptions.filter(
        item =>
          item.code === ErknmKnmDocumentTypeCodesEnum.PP_0 ||
          item.code === ErknmKnmDocumentTypeCodesEnum.PP_I ||
          item.code === ErknmKnmDocumentTypeCodesEnum.PP_II ||
          item.code === ErknmKnmDocumentTypeCodesEnum.PP_III ||
          item.code === ErknmKnmDocumentTypeCodesEnum.PP_IV,
      );
    }
    if (isErknmSystemType) {
      return knmDocumentTypesSelectOptions.filter(item => {
        if (clippedKnmDocumentTypes.includes(item.code)) {
          return false;
        }
        if (
          isSupervisor ||
          (isProsecutorOrSuperProsecutor &&
            (item.code === ErknmKnmDocumentTypeCodesEnum.AP_VI ||
              item.code === ErknmKnmDocumentTypeCodesEnum.AP_VII ||
              item.code === ErknmKnmDocumentTypeCodesEnum.VP_VI))
        ) {
          if (isKnmStatusFillInProcess || isKnmStatusReadyForApprove) {
            switch (item.code) {
              case ErknmKnmDocumentTypeCodesEnum.VP_0:
              case ErknmKnmDocumentTypeCodesEnum.VP_I:
              case ErknmKnmDocumentTypeCodesEnum.VP_II:
              case ErknmKnmDocumentTypeCodesEnum.VP_IV:
              case ErknmKnmDocumentTypeCodesEnum.VP_VI:
                return true;
              default:
                return false;
            }
          }
          if (
            isKnmStatusApproving ||
            isKnmStatusNotApprove ||
            isKnmStatusCommentsExist ||
            isKnmStatusRejectAppealed ||
            isKnmStatusTypeWaitingCarryOut ||
            isKnmStatusTypeCantBeCarryOut ||
            isKnmStatusTypeDeleted
          ) {
            switch (item.code) {
              case ErknmKnmDocumentTypeCodesEnum.VP_VI:
                return true;
              default:
                return false;
            }
          }
          if (isKnmStatusTypeCarriedOut) {
            switch (item.code) {
              case ErknmKnmDocumentTypeCodesEnum.VP_III:
              case ErknmKnmDocumentTypeCodesEnum.AP_0:
              case ErknmKnmDocumentTypeCodesEnum.MZ_0:
              case ErknmKnmDocumentTypeCodesEnum.VP_VI:
                return true;
              default:
                return false;
            }
          }
          if (isKnmStatusTypeCompleted || isKnmStatusDecisionAppealed) {
            switch (item.code) {
              case ErknmKnmDocumentTypeCodesEnum.VP_III:
              case ErknmKnmDocumentTypeCodesEnum.AP_0:
              case ErknmKnmDocumentTypeCodesEnum.MZ_0:
              case ErknmKnmDocumentTypeCodesEnum.AP_VI:
              case ErknmKnmDocumentTypeCodesEnum.AP_VII:
              case ErknmKnmDocumentTypeCodesEnum.VP_VI:
                return true;
              default:
                return false;
            }
          }
        }

        return false;
      });
    }

    return knmDocumentTypesSelectOptions;
  }, [isErknmSystemType, knmDocumentTypesSelectOptions, kindKnm, typeErknm, reasonDocument]);

  const [isVisibleSignModal, setIsVisibleSignModal] = useState<boolean>(false);
  const [formFieldName, setFormFieldName] = useState<string | null>(null);

  const addDocument = useCallback(() => !watchOnly && push && push('documents', { isNew: true, listId: uuidv4() }), [
    push,
    watchOnly,
  ]);

  const addAttachment = useCallback(
    (fieldName: string, data: IKnmAttachmentDocument) => !watchOnly && push && push(fieldName, data),
    [push, watchOnly],
  );

  const handleCloseSignModal = useCallback(() => {
    setIsVisibleSignModal(false);
    setFormFieldName(null);
  }, []);

  const handleOpenSignModal = (fieldName: string) => () => {
    setIsVisibleSignModal(true);
    setFormFieldName(fieldName);
  };

  const handleSaveSignedDocument = useCallback(
    (signedDocument: File, signatureInfo: IKnmAttachment['signatureInfo']) => {
      dispatch(knmDocumentsSaveSignedDocument(form, signedDocument, signatureInfo, formFieldName, addAttachment));
      handleCloseSignModal();
    },
    [addAttachment, dispatch, form, formFieldName, handleCloseSignModal],
  );

  const renderDocumentFields = (
    arrayFieldName: string,
    fields: FieldArrayRenderProps<IKnmFormDocument, HTMLElement>['fields'],
    startIndex: number,
  ) => {
    return fields.map((name: string, index: number) => {
      const fieldValue: IKnmFormDocument = fields.value?.[index];
      const id = getDocumentBlockId(fieldValue.listId);

      if (fieldValue.type?.code === DocumentsTypeCodesEnum.WEB_0) {
        return null;
      }

      const displayIndex = startIndex + index + 1;

      return (
        <KnmCollapse
          theme="fields"
          title={`Документ №${displayIndex}`}
          id={id}
          key={id}
          isNew={fieldValue.isNew}
          opened={true}
          onClose={
            !disableDeleteDocuments && !watchOnly && fieldValue.isNew
              ? () => !watchOnly && remove && remove(arrayFieldName, index)
              : undefined
          }
        >
          <Row>
            <FieldBlock label="Тип документа" color="black">
              <SelectInputField
                className="shared-field_margin-bottom"
                name={`${name}.type`}
                options={filteredKnmDocumentTypesSelectOptions}
                isClearable={false}
                disabled={watchOnly || !fieldValue.isNew}
              />
            </FieldBlock>
            <FieldBlock label="Описание" color="black">
              <TextareaField
                className="shared-field"
                inputClassName={styles.DescriptionInput}
                name={`${name}.description`}
                disabled={watchOnly || !fieldValue.isNew}
              />
            </FieldBlock>
            <FieldBlock
              label="Файлы"
              color="black"
              button={
                <Button
                  className={styles.AddFileButton}
                  text="Добавить"
                  type="button"
                  variant="link"
                  color="primary"
                  id="fileAddButton"
                  onClick={handleOpenSignModal(`${name}.attachments`)}
                  disabled={watchOnly || (fieldValue.attachments && fieldValue.attachments.length > 0)}
                />
              }
            />
            <FormSpy subscription={{ errors: true, touched: true }}>
              {props => {
                if (props.errors?.[arrayFieldName]) {
                  const fieldError = props.errors?.[arrayFieldName]?.[index]?.attachments;
                  const isFieldTouched = props.touched?.[`${arrayFieldName}[${index}].attachments`];

                  const errorMessage: string | undefined = fieldError
                    ? (Object.values(fieldError).reduce((prev: string, curr: string) => prev + curr, '') as string)
                    : undefined;

                  if (isFieldTouched && errorMessage) {
                    return <p className="shared-input-error">Поле не может быть пустым</p>;
                  }
                }

                return null;
              }}
            </FormSpy>
            <Row>
              <KnmAttachmentsList
                name={`${name}.attachments`}
                removeField={remove}
                isWatchOnly={!fieldValue.isNew || watchOnly || disableDeleteDocuments}
                isShowDateTime={true}
              />
            </Row>
          </Row>
        </KnmCollapse>
      );
    });
  };

  return (
    <Section
      title={title}
      id={KNM_DOCUMENTS}
      type="list"
      customButton={
        <>
          <Button
            className="shared-btn-add"
            text="Добавить"
            type="button"
            variant="link"
            color="primary"
            disabled={watchOnly}
            onClick={addDocument}
          />
        </>
      }
    >
      <FieldArray name="documents">
        {({ fields: documentsFields }) => (
          <FieldArray name="additionalDocuments">
            {({ fields: additionalDocumentsFields }) => {
              const totalLength = (documentsFields?.length || 0) + (additionalDocumentsFields?.length || 0);

              if (!totalLength) {
                return null;
              }

              const documentsCount = documentsFields?.length || 0;
              const hasDocuments = Boolean(documentsFields?.length);
              const hasAdditionalDocuments = Boolean(additionalDocumentsFields?.length);

              return (
                <Row>
                  {hasDocuments && documentsFields && renderDocumentFields('documents', documentsFields, 0)}
                  {hasAdditionalDocuments &&
                    additionalDocumentsFields &&
                    renderDocumentFields('additionalDocuments', additionalDocumentsFields, documentsCount)}
                </Row>
              );
            }}
          </FieldArray>
        )}
      </FieldArray>
      <KnmSignDocumentModal
        isVisibleModal={isVisibleSignModal}
        fieldName="documents"
        onCloseSignModal={handleCloseSignModal}
        onSaveSignedDocument={handleSaveSignedDocument}
      />
    </Section>
  );
}

const mapStateToProps = (state: IRootState): IDecisionDocumentsStoreProps => {
  const knmSystemType = getKnmSystemType(state);
  const isProsecutorOrSuperProsecutor = getUserRoleIsProsecutorOrSuperProsecutor(state);
  const isSupervisor = getUserRoleIsSupervisor(state);
  const isKnmStatusFillInProcess = getKnmIsErknmStatusFillInProcess(state);
  const isKnmStatusReadyForApprove = getKnmIsErknmStatusReadyForApprove(state);
  const isKnmStatusApproving = getKnmIsErknmStatusApproving(state);
  const isKnmStatusNotApprove = getKnmIsErknmStatusNotApprove(state);
  const isKnmStatusCommentsExist = getKnmIsErknmStatusCommentsExist(state);
  const isKnmStatusRejectAppealed = getKnmIsErknmStatusRejectAppealed(state);
  const isKnmStatusTypeWaitingCarryOut = getKnmIsErknmStatusTypeWaitingCarryOut(state);
  const isKnmStatusTypeCarriedOut = getKnmIsErknmStatusTypeCarriedOut(state);
  const isKnmStatusTypeCompleted = getKnmIsErknmStatusTypeCompleted(state);
  const isKnmStatusDecisionAppealed = getKnmIsErknmStatusDecisionAppealed(state);
  const isKnmStatusTypeCantBeCarryOut = getKnmIsErknmStatusTypeCantBeCarryOut(state);
  const isKnmStatusTypeDeleted = getKnmIsErpStatusDeleted(state);

  return {
    knmSystemType,
    isProsecutorOrSuperProsecutor,
    isSupervisor,
    isKnmStatusFillInProcess,
    isKnmStatusReadyForApprove,
    isKnmStatusApproving,
    isKnmStatusNotApprove,
    isKnmStatusCommentsExist,
    isKnmStatusRejectAppealed,
    isKnmStatusTypeWaitingCarryOut,
    isKnmStatusTypeCarriedOut,
    isKnmStatusTypeCompleted,
    isKnmStatusDecisionAppealed,
    isKnmStatusTypeCantBeCarryOut,
    isKnmStatusTypeDeleted,
    knmDocumentTypesSelectOptions: getCatalogDecisionDocumentTypesSelectOptions(state),
  };
};

export const DecisionDocuments = connect(mapStateToProps)(DecisionDocumentsWithoutStore);
